import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Privacy = () => (
  <div className="min-h-screen bg-[#FDFBF7] paper-texture">
    <nav className="bg-white/80 backdrop-blur-xl border-b border-[#E1D9CD]">
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-[#6B7A4F]" />
          <span className="text-xl font-bold text-[#2D2A26]">Bookish Bestie</span>
        </Link>
        <Link to="/">
          <Button variant="ghost" size="sm" className="text-[#5C5955]">
            <ArrowLeft className="w-4 h-4 mr-2" />Home
          </Button>
        </Link>
      </div>
    </nav>

    <div className="container mx-auto px-6 py-12 max-w-3xl">
      <div className="glass-card p-8 sm:p-12 rounded-2xl">
        <div className="text-center mb-8">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#6B7A4F]">A Built for Bookish Experience</span>
          <h1 className="text-4xl font-light text-[#2D2A26] mt-3">Privacy <span className="font-bold">Policy</span></h1>
          <p className="text-sm text-[#5C5955] mt-2">Last updated: February 2026</p>
        </div>

        <div className="prose prose-sm max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-3">What We Collect</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#5C5955]">
              <li><strong>Account info:</strong> name, email, hashed password</li>
              <li><strong>Profile info:</strong> bio, city, favorite genres, reading preferences, books you've added</li>
              <li><strong>Location:</strong> approximate latitude/longitude (used for distance-based matching)</li>
              <li><strong>Interactions:</strong> matches viewed, connection requests, in-app messages, book club posts</li>
              <li><strong>Payment info:</strong> handled entirely by Stripe. We store only your payment status, never your card details</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">How We Use Your Info</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#5C5955]">
              <li>Generate compatibility matches based on your genres, books, and location</li>
              <li>Enable in-app messaging between connected users</li>
              <li>Send password reset emails and (optionally) email verification links</li>
              <li>Improve the Service</li>
            </ul>
            <p className="text-[#5C5955] mt-3">
              <strong>We never sell your personal information to third parties.</strong>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Who Can See What</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#5C5955]">
              <li>Your <strong>name, bio, city, favorite genres, and books</strong> are visible to other users on the Discover page and to your matches</li>
              <li>Your <strong>exact latitude/longitude</strong> is never shown to other users — only used server-side for distance calculations</li>
              <li>Your <strong>email</strong> is never shown to other users</li>
              <li>Your <strong>messages</strong> are only visible to you and the person you're chatting with</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Third-Party Services</h2>
            <p className="text-[#5C5955] leading-relaxed">
              We use Stripe for payment processing, Open Library for book search and cover images, OpenStreetMap
              Nominatim for reverse geocoding, and OpenAI (via emergent) for AI-powered match suggestions. Each
              service has its own privacy policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Your Choices</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#5C5955]">
              <li>You can update or delete your profile info at any time from your Profile page</li>
              <li>You can request account deletion via our social channels @BuiltForBookish (note: paid access does not entitle you to a refund)</li>
              <li>You may choose not to share your location, but doing so limits location-based matching</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Data Security</h2>
            <p className="text-[#5C5955] leading-relaxed">
              We use industry-standard security practices including bcrypt password hashing, httpOnly cookies for
              authentication, and encrypted transit (HTTPS). However, no system is 100% secure. Use a strong,
              unique password.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Children</h2>
            <p className="text-[#5C5955] leading-relaxed">
              Bookish Bestie is intended for users 18 and older. We do not knowingly collect information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">Contact</h2>
            <p className="text-[#5C5955] leading-relaxed">
              Privacy questions? Reach out via our social channels @BuiltForBookish.
            </p>
          </section>
        </div>
      </div>
    </div>
  </div>
);

export default Privacy;
